
import { defineComponent, ref, computed, reactive, watch, PropType } from 'vue'

export default defineComponent({
  name: 'BacktestPeriod',
  props: {
    isLight: Boolean,
    colors: { type: Object as PropType<Record<string, string>>, required: true },
    initialValue: { type: Object as PropType<any>, default: null },
    comOptions: { type: Object as PropType<any>, default: null }
  },
  emits: ['change'],
  setup(props, { emit }) {
    const period = ref('1y')
    const startDate = ref('')
    const endDate = ref('')
    const today = new Date().toISOString().slice(0, 10)
    const minDate = (() => {
      const d = new Date()
      d.setFullYear(d.getFullYear() - 2)
      return d.toISOString().slice(0, 10)
    })()

    // 日历状态
    const showCalendar = ref(false)
    const calPopupStyle = reactive({ top: '0px', left: '0px' })
    const now = new Date()
    const calYear = ref(now.getFullYear())
    const calMonth = ref(now.getMonth())
    const tempStart = ref('')
    const tempEnd = ref('')
    const pickStep = ref<'start' | 'end'>('start')

    const emitInitial = () => {
      const first = periodOptions.value.find(o => o.value === '1y') || periodOptions.value[0]
      emit('change', { period: first?.code || 'year1', periodLabel: first?.label || '近1年', periodStart: null, periodEnd: null, valid: true })
    }

    const periodOptions = computed(() => {
      if (props.comOptions?.periodList?.length) {
        return props.comOptions.periodList.map((o: any) => ({
          label: o.name,
          value: o.code === 'month3' ? '3m' : o.code === 'month6' ? '6m' : o.code === 'year1' ? '1y' : o.code === 'year2' ? '2y' : o.code,
          code: o.code,
        }))
      }
      return [
        { label: '近3月', value: '3m', code: 'month3' },
        { label: '近6月', value: '6m', code: 'month6' },
        { label: '近1年', value: '1y', code: 'year1' },
        { label: '近2年', value: '2y', code: 'year2' },
      ]
    })

    const periodLabel = computed(() => {
      if (period.value === 'custom' && startDate.value && endDate.value) {
        return `${startDate.value}~${endDate.value}`
      }
      return periodOptions.value.find(o => o.value === period.value)?.label || '--'
    })

    const selectPeriod = (v: string) => {
      period.value = v
      if (v !== 'custom') {
        startDate.value = ''
        endDate.value = ''
        showCalendar.value = false
      }
      const code = periodOptions.value.find(o => o.value === v)?.code || v
      emit('change', {
        period: code,
        periodLabel: periodLabel.value,
        periodStart: null,
        periodEnd: null,
        valid: v !== 'custom',
      })
    }

    const openCalendar = (e: MouseEvent) => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      const popupH = 320
      const spaceBelow = window.innerHeight - rect.bottom - 130
      const top = spaceBelow >= popupH ? rect.bottom + 6 : rect.top - popupH - 6
      calPopupStyle.top = top + window.scrollY + 'px'
      calPopupStyle.left = Math.min(rect.left, window.innerWidth - 260) + window.scrollX + 'px'
      tempStart.value = startDate.value
      tempEnd.value = endDate.value
      pickStep.value = 'start'
      showCalendar.value = true
    }

    const closeCalendar = () => { showCalendar.value = false }

    const prevMonth = () => {
      if (calMonth.value === 0) { calMonth.value = 11; calYear.value-- }
      else calMonth.value--
    }
    const nextMonth = () => {
      if (calMonth.value === 11) { calMonth.value = 0; calYear.value++ }
      else calMonth.value++
    }

    // 生成日历格子
    const calCells = computed(() => {
      const year = calYear.value
      const month = calMonth.value
      const firstDay = new Date(year, month, 1).getDay() // 0=周日
      const offset = firstDay === 0 ? 6 : firstDay - 1  // 转为周一起始
      const daysInMonth = new Date(year, month + 1, 0).getDate()
      const cells: { day: number | ''; date: string }[] = []
      for (let i = 0; i < offset; i++) cells.push({ day: '', date: '' })
      for (let d = 1; d <= daysInMonth; d++) {
        const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
        cells.push({ day: d, date })
      }
      return cells
    })

    const getCellClass = (cell: { day: number | ''; date: string }) => {
      if (!cell.date) return 'cal-cell--empty'
      const d = cell.date
      const s = tempStart.value
      const e = tempEnd.value
      const isToday = d === today
      const isStart = d === s
      const isEnd = d === e
      const inRange = s && e && d > s && d < e
      const isFuture = d > today
      const isPast2y = d < minDate
      return {
        'cal-cell--today': isToday,
        'cal-cell--start': isStart,
        'cal-cell--end': isEnd,
        'cal-cell--in-range': inRange,
        'cal-cell--disabled': isFuture || isPast2y,
      }
    }

    const onCellClick = (date: string) => {
      if (date > today || date < minDate) return
      if (pickStep.value === 'start') {
        tempStart.value = date
        tempEnd.value = ''
        pickStep.value = 'end'
      } else {
        if (date < tempStart.value) {
          tempEnd.value = tempStart.value
          tempStart.value = date
        } else {
          tempEnd.value = date
        }
        pickStep.value = 'start'
      }
    }

    const clearDates = () => {
      tempStart.value = ''
      tempEnd.value = ''
      pickStep.value = 'start'
    }

    const confirmDates = () => {
      if (!tempStart.value || !tempEnd.value) return
      startDate.value = tempStart.value
      endDate.value = tempEnd.value
      showCalendar.value = false
      const start = new Date(startDate.value).getTime()
      const end = new Date(endDate.value + 'T23:59:59').getTime()
      emit('change', {
        period: 'custom',
        periodLabel: periodLabel.value,
        periodStart: start,
        periodEnd: end,
        valid: true,
      })
    }

    const tagStyle = (selected: boolean) => ({
      background: selected ? '#FFEDED' : (props.isLight ? '#f0f0f0' : '#2a2a2a'),
      color: selected ? '#1a1a1a' : props.colors.textPrimary,
      borderColor: selected ? '#333' : 'transparent',
    })

    emitInitial()

    // 口令回显
    watch(() => props.initialValue, (val) => {
      if (!val) return
      const reverseMap: Record<string, string> = {
        month3: '3m', month6: '6m', year1: '1y', year2: '2y', custom: 'custom'
      }
      const p = reverseMap[val.period] || '1y'
      period.value = p
      let resolvedStart = ''
      let resolvedEnd = ''
      if (p === 'custom' && val.periodStart && val.periodEnd) {
        resolvedStart = new Date(val.periodStart).toISOString().slice(0, 10)
        resolvedEnd = new Date(val.periodEnd).toISOString().slice(0, 10)
        startDate.value = resolvedStart
        endDate.value = resolvedEnd
      }
      const label = p === 'custom' && resolvedStart && resolvedEnd
        ? `${resolvedStart}~${resolvedEnd}`
        : periodOptions.value.find(o => o.value === p)?.label || '--'
      emit('change', {
        period: val.period || 'month3',
        periodLabel: label,
        periodStart: val.periodStart || null,
        periodEnd: val.periodEnd || null,
        valid: true,
      })
    }, { immediate: true })
    return {
      period, startDate, endDate, today, periodOptions, periodLabel,
      selectPeriod, tagStyle,
      showCalendar, calPopupStyle, calYear, calMonth, calCells,
      tempStart, tempEnd, pickStep,
      openCalendar, closeCalendar, prevMonth, nextMonth,
      getCellClass, onCellClick, clearDates, confirmDates,
    }
  }
})
